import React, { useEffect, useRef, useState } from "react"
import { CardImage } from "react-bootstrap-icons"
import { Button } from "../shared/Button"
import TaggableInput from "../shared/fields/TaggableInput"
import { PostListItemStyles } from "../posts/constants"
import { TextAsImage } from "../posts/TextAsImage"
import { LinkPreview } from "../posts/LinkPreview"
import { Spinner } from "../shared/Spinner"
import { useBandPosts } from "../../hooks/useBandPosts"
import ImagesUpload from "../ImagesUpload"
import { RelatedPostFormField } from "./RelatedPostFormField"
import {
  createHeadlinePost,
  updateHeadlinePost,
  uploadImages,
} from "../../util/backend_api/posts"
import { fetchUrlUnfurl } from "../../util/backend_api/urlUnfurl"
import { convertHeicToJpeg } from "../../util/heicConversion"
import PostDeleteButton from "../PostDeleteButton"
import { useQueryClient } from "react-query"
import { toast } from "react-toastify"
import { root_path } from "../../routes"
import { ImagePreview } from "../../types/posts"

interface AttachedMediaItem {
  signed_id?: string
  filename: string
  content_type_generic: string
  content_type_specific: string
  url: string
}

interface HeadlinePost {
  title?: string
  id?: number | null
  related_post_id?: number | null
  original_post_text?: string
  attached_media?: AttachedMediaItem[]
}

interface UnfurledObject {
  url: string
  title?: string
  best_title?: string
  best_image?: string
  description?: string
  host?: string
  postType?: string
  link_preview?: {
    best_title?: string
    best_image?: string
    description?: string
    host?: string
  }
}

interface HeadlinePostFormProps {
  bandId: number
  post?: HeadlinePost
  setShowForm?: ((value: boolean) => void) | null
  setNewPost?: ((post: any) => void) | null
  onDeleted?: () => void
}

const MAX_URL_LENGTH = 250

const headlineProps: HeadlinePost = {
  title: "",
  id: null,
  related_post_id: null,
}

const HeadlinePostForm: React.FC<HeadlinePostFormProps> = ({
  bandId,
  post = headlineProps,
  setShowForm = null,
  setNewPost = null,
  onDeleted,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [btnActive, setBtnActive] = useState(true)
  const [headlineText, setHeadlineText] = useState<string>(
    post.original_post_text || "",
  )
  // Initialize with existing related post if editing
  const [selectedRelatedPosts, setSelectedRelatedPosts] = useState<any[]>(
    post.related_post_id
      ? [
          {
            id: post.related_post_id,
            // We'll need to fetch the full post data if needed for display
          },
        ]
      : [],
  )
  const [postId, setPostId] = useState<number | null | undefined>(post.id)
  const [formError, setFormError] = useState<string | null>(null)
  const [unfurledObj, setUnfurledObj] = useState<UnfurledObject | null>(null)
  const [imageFiles, setImageFiles] = useState<any[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const queryClient = useQueryClient()
  const [imagePreviews, setImagePreviews] = useState<ImagePreview[]>(
    post.attached_media
      ? post.attached_media.map((image) => ({
          signedId: image.signed_id,
          filename: image.filename,
          contentTypeGeneric: image.content_type_generic,
          contentTypeSpecific: image.content_type_specific,
          url: image.url,
        }))
      : [],
  )

  const { data: relatablePostResult, isLoading: isLoadingRelatablePosts } =
    useBandPosts(bandId, "relatable_content", false, 1, 50) as {
      data:
        | {
            total_count: number
            posts: Array<{
              id: number
              title: string
              post_type: string
              published_at: string
              view_on?: string
            }>
          }
        | undefined
      isLoading: boolean
    }

  const onSubmit = async () => {
    setBtnActive(false)
    const contentHash: {
      headline_text: string
      link_preview?: UnfurledObject
    } = { headline_text: headlineText }
    let postType = "headline"
    if (unfurledObj) {
      postType = unfurledObj.postType || "headline"
      const unfurledObjCopy = { ...unfurledObj }
      delete unfurledObjCopy.postType
      contentHash.link_preview = unfurledObjCopy
    }
    try {
      let imageUploadSignedIds: string[] = []
      if (imagePreviews.length > 0) {
        const uploadResponse = await uploadImages(
          imagePreviews,
          setImagePreviews,
        )
        if (uploadResponse.errors.length > 0) {
          setFormError(uploadResponse.errors.join(", "))
          return
        } else {
          imageUploadSignedIds = uploadResponse.signedIds
          postType = "image_album"
        }
      }
      // Use the first selected related post's ID, or empty string if none selected
      const relatedPostId =
        selectedRelatedPosts.length > 0
          ? selectedRelatedPosts[0].id.toString()
          : ""

      const response = postId
        ? await updateHeadlinePost(
            bandId,
            postId,
            postType,
            contentHash,
            relatedPostId,
            imageUploadSignedIds,
          )
        : await createHeadlinePost(
            bandId,
            postType,
            contentHash,
            relatedPostId,
            imageUploadSignedIds,
          )
      if (response.error) {
        setFormError(
          response.error.message
            ? response.error.message
            : String(response.error),
        )
      } else if (
        response.post &&
        (response.post.title || response.post.link_preview?.best_title)
      ) {
        // only refetch if this is a brand new post. Edited posts update inline.
        if (!postId) {
          setTimeout(() => {
            queryClient.invalidateQueries(["posts"])
          }, 2500)
        }
        if (setShowForm && setNewPost) {
          setNewPost(response.post)
          setShowForm(false)
        } else {
          // this means we are on the dedicated posts page, so redirect to the feed
          toast.success("Thanks for the new Post! (sending you there in...)", {
            autoClose: 2000,
            onClose: () => {
              window.location.href = response.post.nada_url
            },
          })
        }
      }
    } catch (e) {
      console.error(e)
      setFormError("Sorry, there was an unknown error. Please try again.")
      setBtnActive(true)
    }
  }

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      handleHeadlineTextChange(headlineText)
    }, 1500)

    return () => clearTimeout(delayDebounceFn)
  }, [headlineText])

  const handleHeadlineTextChange = async (newText: string) => {
    if (!newText) return
    setBtnActive(false)
    // regular expression used to check for URLs in the user's headline post text as they're typing it
    // if we find a match, we send the URL to the backend in order to unfurl it and retrieve data that
    //   is necessary for building a link preview of the URL
    const urlRegex = new RegExp(
      /https?:\/\/[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)?/gi,
    )
    const youtubeVideoUrlRegex = /youtube\.com\/(?:watch\?v=|shorts\/)/i
    const vimeoVideoUrlRegex = /vimeo\.com\/(\d+)/i
    const dropboxVideoUrlRegex =
      /^https?:\/\/(?:www\.)?dropbox\.com\/.*\.mp4(?:\?.*)?$/i
    const imageUrlRegex = /(.*\.(png|jpg|gif|jpeg)(\?|$))/i
    const matches = newText.replaceAll("amp;", "").match(urlRegex)
    if (matches) {
      try {
        const url =
          matches[0].length > MAX_URL_LENGTH
            ? matches[0].slice(0, MAX_URL_LENGTH)
            : matches[0]
        const response = await fetchUrlUnfurl(url)
        const link_meta: UnfurledObject = response.link_meta
        if (!link_meta.url.match(/https?:\/\//)) {
          link_meta.url = `https://${link_meta.url}`
        }
        if (link_meta.url.length > MAX_URL_LENGTH) {
          link_meta.url = link_meta.url.slice(0, MAX_URL_LENGTH)
        }
        if (link_meta.url.match(youtubeVideoUrlRegex)) {
          link_meta.postType = "youtube_video"
        } else if (link_meta.url.match(dropboxVideoUrlRegex)) {
          link_meta.postType = "link_video"
        } else if (link_meta.url.match(vimeoVideoUrlRegex)) {
          link_meta.postType = "link_video"
        } else if (link_meta.url.match(imageUrlRegex)) {
          link_meta.best_image = link_meta.url
          link_meta.postType = "link_image"
        } else {
          link_meta.postType = "link"
        }
        setUnfurledObj(link_meta)
      } catch (err) {
        setUnfurledObj(null)
        console.error("error with unfurl: ", err)
      }
    } else {
      setUnfurledObj(null)
    }
    setBtnActive(true)
  }

  const getPreviewText = (text: string | undefined): string => {
    if (!text) return "Your Headline Here"
    // End of sentence punctuation: . ! ? (and optionally ; : — – -)
    const punctuationPattern = /[.!?]/
    const match = text.match(punctuationPattern)
    if (match && match.index !== undefined) {
      return text.substring(0, match.index + 1)
    }
    return text
  }

  return (
    <>
      <div className="grid grid-cols-12 mb-6">
        <div className={PostListItemStyles.contentDiv}>
          {unfurledObj ? (
            <LinkPreview
              defaultImage={unfurledObj.best_image || ""}
              unfurledObj={{
                title: unfurledObj.best_title || "",
                best_title: unfurledObj.best_title || "",
                best_image: unfurledObj.best_image || "",
                description: unfurledObj.description || "",
                host: unfurledObj.host || "",
              }}
            />
          ) : btnActive ? (
            <TextAsImage text={getPreviewText(headlineText)} />
          ) : (
            <div className="bg-black flex h-full items-center justify-center text-center">
              <Spinner className="text-white" />
            </div>
          )}
        </div>

        <div className={PostListItemStyles.metaDiv}>
          <form>
            <label
              className="block col-span-12 text-gray-900 text-xs uppercase font-bold"
              htmlFor="post"
            >
              Post anything
            </label>
            <TaggableInput
              value={headlineText}
              onChange={(val) => setHeadlineText(val)}
              placeholder="Add any text and/or links here"
              className="headline-post-form"
            />
            <div className="pt-1">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                aria-label="Attach images"
                disabled={isSubmitting}
              >
                <CardImage size={18} />
              </button>
            </div>
            <ImagesUpload
              setImageFiles={setImageFiles}
              imageFiles={imageFiles}
              imagePreviews={imagePreviews}
              setImagePreviews={setImagePreviews}
              triggerRef={fileInputRef}
              hideTriggerButton={true}
              preprocessFile={convertHeicToJpeg}
            />
            <RelatedPostFormField
              onPostSelect={setSelectedRelatedPosts}
              selectedPosts={selectedRelatedPosts}
            />
            <div className="col-span-12 text-red-700 lg:text-sm text-lg">
              {formError}
            </div>

            <div className="mt-8">
              {post.id ? (
                <>
                  <Button
                    disabled={!btnActive}
                    className="mr-2 mb-1"
                    bg="bg-green-700"
                    onClick={onSubmit}
                  >
                    Update Post
                  </Button>
                  <PostDeleteButton
                    postId={post.id}
                    bandId={bandId}
                    setShowForm={setShowForm || (() => { window.location.href = root_path() })}
                    onDeleted={onDeleted}
                    queryKey={["posts"]}
                  />
                </>
              ) : (
                <Button
                  disabled={!btnActive || !headlineText}
                  bg="bg-green-700"
                  onClick={onSubmit}
                  className="mr-2"
                >
                  Make Post
                </Button>
              )}

              <Button
                disabled={!btnActive}
                bg="bg-gray-400"
                onClick={() => (window.location.href = root_path())}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

export default HeadlinePostForm
